import { getLocale, isLocale, setLocale } from "@plannello/i18n/runtime";
import { useSyncExternalStore } from "react";
import type { Locale } from "./queries";

const listeners = new Set<() => void>();
let version = 0;
let pendingFocusId: string | null = null;

/** Language names are shown in their own language, never translated. */
export const LOCALES: ReadonlyArray<{ value: Locale; label: string }> = [
  { value: "en", label: "English" },
  { value: "de", label: "Deutsch" },
];

function syncDocument() {
  document.documentElement.setAttribute("lang", getLocale());
}

/** Switch the UI language live (no reload). Subscribers re-render; `<html lang>` follows. */
export function applyLocale(next: string, opts: { refocusId?: string } = {}) {
  if (!isLocale(next) || next === getLocale()) return;
  void setLocale(next, { reload: false });
  syncDocument();
  pendingFocusId = opts.refocusId ?? null;
  version++;
  for (const l of listeners) l();
}

/** Called once before first render: honour an existing cookie, else the browser language. */
export function initLocale() {
  const hasCookie = document.cookie.split("; ").some((c) => c.startsWith("PARAGLIDE_LOCALE="));
  if (!hasCookie) {
    const guess = (navigator.language || "en").slice(0, 2);
    if (isLocale(guess)) void setLocale(guess, { reload: false });
  }
  syncDocument();
}

/** Re-renders the subscriber when the locale changes. Returns the current locale. */
export function useLocale(): Locale {
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    () => version,
  );
  return getLocale() as Locale;
}

/** After a live locale switch the tree remounts; restore focus to the control that caused it. */
export function consumePendingFocus(): string | null {
  const id = pendingFocusId;
  pendingFocusId = null;
  return id;
}
